import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, Upload, Button, Table, Select, message, Alert, Space,
  Tag, Typography, Input, InputNumber, Tooltip,
} from "antd";
import {
  InboxOutlined, WarningOutlined, CheckCircleOutlined, DeleteOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import {
  createInvoiceWithFile, getAllPartners, getAccountTree, getAllCategories,
} from "../../../api/accounting";

const { Dragger } = Upload;
const { Option, OptGroup } = Select;
const { Text } = Typography;

const EXPECTED_COLUMNS = {
  bill_number:  { label: "Bill/Invoice Number", aliases: ["bill number", "invoice number", "number", "bill no", "invoice no", "bill #", "invoice #"] },
  client:       { label: "Client",              aliases: ["client"] },
  partner_name: { label: "Partner",              aliases: ["vendor", "customer", "partner", "supplier"] },
  brand:        { label: "Brand",               aliases: ["brand", "brand name"] },
  issue_date:   { label: "Issue Date",          aliases: ["issue date", "date", "bill date", "invoice date"] },
  product_type: { label: "Product Type",        aliases: ["product type", "type", "item type"] },
  category:     { label: "Category",            aliases: ["category"] },
  account_name: { label: "Chart of Account",    aliases: ["chart of account", "chart of accounts", "account", "account name", "coa"] },
  product_name: { label: "Product Name",        aliases: ["product name", "product", "item", "item name"] },
  description:  { label: "Description",         aliases: ["description", "desc", "memo", "notes"] },
  quantity:     { label: "Quantity",             aliases: ["quantity", "qty", "units"] },
  unit_price:   { label: "Unit Price",          aliases: ["unit price", "price", "rate", "unit cost", "cost"] },
  due_date:     { label: "Due Date",            aliases: ["due date", "payment due", "due"] },
  total:        { label: "Total",               aliases: ["total", "total amount", "amount", "grand total", "sum"] },
  currency:     { label: "Currency",            aliases: ["currency", "curr", "ccy"] },
};

function autoMapColumns(excelColumns) {
  const mapping = {};
  for (const [field, config] of Object.entries(EXPECTED_COLUMNS)) {
    mapping[field] = null;
    for (const alias of config.aliases) {
      const match = excelColumns.find(c => c.toLowerCase().trim() === alias);
      if (match) { mapping[field] = match; break; }
    }
  }
  return mapping;
}

function getVal(row, mapping, defaults, field, fallback = "") {
  const colVal = mapping[field] ? (row[mapping[field]] || "").toString().trim() : "";
  return colVal || (defaults[field] || "").toString().trim() || fallback;
}

function groupRowsIntoBills(rows, mapping, defaults = {}) {
  const bills = [];
  let currentBill = null;

  for (const row of rows) {
    const billNum = getVal(row, mapping, defaults, "bill_number");
    const partnerVal = getVal(row, mapping, defaults, "partner_name");
    const clientVal = getVal(row, mapping, defaults, "client");
    const issueVal = getVal(row, mapping, defaults, "issue_date");

    const hasPartnerInRow = (mapping.partner_name && (row[mapping.partner_name] || "").toString().trim() !== "") || (mapping.client && (row[mapping.client] || "").toString().trim() !== "");
    const isNewBill = (mapping.bill_number && billNum !== "") || ((partnerVal !== "" || clientVal !== "") && issueVal !== "" && hasPartnerInRow) || !currentBill;

    if (isNewBill) {
      currentBill = {
        key: `bill-${bills.length}`,
        number: billNum || `BATCH-${bills.length + 1}`,
        partner_name: partnerVal,
        client_name: clientVal,
        display_name: partnerVal || clientVal,
        brand_name: getVal(row, mapping, defaults, "brand"),
        issue_date: parseExcelDate(issueVal) || issueVal,
        due_date: "",
        currency: getVal(row, mapping, defaults, "currency", "USD"),
        spreadsheet_total: null,
        partner_id: null,
        items: [],
        errors: [],
        status: "pending",
      };
      bills.push(currentBill);
    }

    const rowDueDate = getVal(row, mapping, defaults, "due_date");
    const parsedRowDueDate = rowDueDate ? (parseExcelDate(rowDueDate) || rowDueDate) : "";
    if (parsedRowDueDate) currentBill.due_date = parsedRowDueDate;

    const rowIssueDate = getVal(row, mapping, defaults, "issue_date");
    const parsedRowIssueDate = rowIssueDate ? (parseExcelDate(rowIssueDate) || rowIssueDate) : "";

    const rowTotal = mapping.total ? parseFloat(row[mapping.total]) : NaN;
    if (!isNaN(rowTotal) && rowTotal > 0) currentBill.spreadsheet_total = rowTotal;

    const rowCurrency = mapping.currency ? (row[mapping.currency] || "").toString().trim() : "";
    if (rowCurrency) currentBill.currency = rowCurrency;

    const qty = parseFloat(getVal(row, mapping, defaults, "quantity", "1")) || 1;
    const price = parseFloat(getVal(row, mapping, defaults, "unit_price", "0")) || 0;
    const subtotal = qty * price;

    currentBill.items.push({
      key: `item-${currentBill.items.length}`,
      product_type: getVal(row, mapping, defaults, "product_type", "product").toLowerCase(),
      category_name: getVal(row, mapping, defaults, "category"),
      category_id: null,
      account_name: getVal(row, mapping, defaults, "account_name"),
      account_id: null,
      product_name: getVal(row, mapping, defaults, "product_name"),
      description: getVal(row, mapping, defaults, "description"),
      quantity: qty,
      unit_price: price,
      subtotal,
      total: subtotal,
      due_date: parsedRowDueDate,
      issue_date: parsedRowIssueDate || currentBill.issue_date,
    });
  }

  return bills;
}

function parseExcelDate(val) {
  if (!val && val !== 0) return null;

  if (val instanceof Date) {
    const noon = new Date(val.getTime() + 12 * 60 * 60 * 1000);
    const y = noon.getUTCFullYear();
    const m = String(noon.getUTCMonth() + 1).padStart(2, "0");
    const d = String(noon.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  if (typeof val === "number") {
    const serial = Math.floor(val);
    const adjusted = serial > 60 ? serial - 1 : serial;
    const baseUTC = Date.UTC(1900, 0, 1);
    const date = new Date(baseUTC + (adjusted - 1) * 86400000);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const str = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const formats = [
    "YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY", "M/D/YYYY", "D/M/YYYY",
    "DD-MMM-YYYY", "D-MMM-YYYY", "MMM D, YYYY", "MMMM D, YYYY",
    "DD.MM.YYYY", "YYYY/MM/DD", "MM-DD-YYYY", "DD-MM-YYYY",
  ];
  for (const fmt of formats) {
    const d = dayjs(str, fmt, true);
    if (d.isValid()) return d.format("YYYY-MM-DD");
  }
  const fallback = dayjs(str);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD") : str;
}

function flattenAccountTree(nodes, depth = 0) {
  const flat = [];
  if (!nodes) return flat;
  for (const n of nodes) {
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    const selectable = !!(n.is_postable || !hasChildren);
    flat.push({ ...n, depth, selectable, hasChildren });
    if (hasChildren) flat.push(...flattenAccountTree(n.children, depth + 1));
  }
  return flat;
}

const BatchUploadBillsInvoices = ({
  visible, onCancel, onSuccess, selectedBrand, invoiceType = "bill",
}) => {
  const isBill = invoiceType === "bill";
  const typeLabel = isBill ? "Bill" : "Invoice";

  const [step, setStep] = useState(1);
  const [fileList, setFileList] = useState([]);
  const [excelRows, setExcelRows] = useState([]);
  const [excelColumns, setExcelColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [bills, setBills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [flatAccounts, setFlatAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);

  useEffect(() => {
    if (!visible) {
      setStep(1); setFileList([]); setExcelRows([]); setExcelColumns([]);
      setColumnMapping({}); setDefaultValues({}); setBills([]); setSaving(false); setResults(null);
      setDataLoaded(false); setLoadErrors([]);
      return;
    }
    const load = async () => {
      setDataLoaded(false);
      setLoadErrors([]);
      const errors = [];
      const brandId = selectedBrand?.id;
      const filters = brandId ? { brand: brandId } : {};

      const [pRes, aRes, cRes] = await Promise.allSettled([
        getAllPartners(filters),
        getAccountTree({}),
        getAllCategories(filters),
      ]);

      try {
        if (pRes.status === "rejected") throw pRes.reason;
        const pData = pRes.value;
        let rawPartners = [];
        if (pData) {
          rawPartners = Array.isArray(pData) ? pData
            : Array.isArray(pData.data) ? pData.data
            : pData.data?.results || pData.results || [];
        }
        if (isBill) {
          rawPartners = rawPartners.filter(p => p.partner_type === "vendor" || p.partner_type === "both");
        } else {
          rawPartners = rawPartners.filter(p => p.partner_type === "customer" || p.partner_type === "both");
        }
        setPartners(rawPartners);
        console.log("[BatchUpload] Loaded partners:", rawPartners.length, rawPartners.slice(0, 5).map(p => p.name));
      } catch (err) {
        console.error("Failed to load partners:", err);
        errors.push("Partners");
      }

      try {
        if (aRes.status === "rejected") throw aRes.reason;
        const aData = aRes.value;
        let acctTree = [];
        if (aData) {
          if (Array.isArray(aData)) acctTree = aData;
          else if (aData?.data && Array.isArray(aData.data)) acctTree = aData.data;
          else if (aData?.results && Array.isArray(aData.results)) acctTree = aData.results;
        }
        setAccounts(acctTree);
        const flat = flattenAccountTree(acctTree);
        setFlatAccounts(flat);
        console.log("[BatchUpload] Loaded accounts:", flat.length, "selectable:", flat.filter(a => a.selectable).length);
      } catch (err) {
        console.error("Failed to load accounts:", err);
        errors.push("Chart of Accounts");
      }

      try {
        if (cRes.status === "rejected") throw cRes.reason;
        const cData = cRes.value;
        const cats = cData?.data || cData;
        setCategories(Array.isArray(cats) ? cats : (cats?.results || []));
      } catch (err) {
        console.error("Failed to load categories:", err);
        errors.push("Categories");
      }
      setLoadErrors(errors);
      setDataLoaded(true);
    };
    load();
  }, [visible, selectedBrand?.id, isBill]);

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!json.length) { message.error("File is empty"); return; }
        const cols = Object.keys(json[0]);
        const dateColKeys = new Set();
        const lowerCols = cols.map(c => c.toLowerCase().trim());
        lowerCols.forEach((lc, i) => {
          if (lc.includes("date") || lc.includes("due") || lc.includes("issue")) dateColKeys.add(cols[i]);
        });
        const cleaned = json.map(row => {
          const out = {};
          for (const key of cols) {
            const v = row[key];
            if (v instanceof Date || (dateColKeys.has(key) && typeof v === "number" && v > 1000)) {
              out[key] = parseExcelDate(v);
            } else {
              out[key] = v;
            }
          }
          return out;
        });
        setExcelRows(cleaned);
        setExcelColumns(cols);
        setColumnMapping(autoMapColumns(cols));
        setStep(2);
      } catch (err) {
        message.error("Failed to parse file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleMappingChange = (field, excelCol) => {
    setColumnMapping(prev => ({ ...prev, [field]: excelCol || null }));
  };

  const fuzzyFind = (needle, haystack) => {
    if (!needle) return null;
    const n = needle.toLowerCase().trim();
    if (haystack[n]) return haystack[n];
    for (const [key, id] of Object.entries(haystack)) {
      if (key.includes(n) || n.includes(key)) return id;
    }
    return null;
  };

  const resolvePartnerAndAccounts = (grouped) => {
    const partnerMap = {};
    partners.forEach(p => {
      partnerMap[(p.name || "").toLowerCase().trim()] = p.id;
      if (p.company_name) partnerMap[p.company_name.toLowerCase().trim()] = p.id;
      if (p.display_name) partnerMap[p.display_name.toLowerCase().trim()] = p.id;
    });

    const accountMap = {};
    flatAccounts.forEach(a => {
      if (!a.selectable) return;
      accountMap[(a.name || "").toLowerCase().trim()] = a.id;
      if (a.code) accountMap[a.code.toString().toLowerCase().trim()] = a.id;
      const codeName = a.code ? `${a.code} - ${a.name}` : "";
      if (codeName) accountMap[codeName.toLowerCase().trim()] = a.id;
    });

    const catMap = {};
    categories.forEach(c => {
      catMap[(c.name || "").toLowerCase().trim()] = c.id;
    });

    console.log("[BatchUpload] Resolve — partners:", partners.length, "accounts:", flatAccounts.length, "categories:", categories.length);
    console.log("[BatchUpload] partnerMap keys:", Object.keys(partnerMap).slice(0, 20));
    console.log("[BatchUpload] accountMap keys:", Object.keys(accountMap).slice(0, 20));

    return grouped.map(bill => {
      const pid = fuzzyFind(bill.partner_name, partnerMap)
        || fuzzyFind(bill.client_name, partnerMap);
      if (!pid) console.warn("[BatchUpload] Partner not found:", JSON.stringify(bill.partner_name), "| client:", JSON.stringify(bill.client_name));

      const resolvedItems = bill.items.map(item => {
        const aid = fuzzyFind(item.account_name, accountMap);
        const cid = fuzzyFind(item.category_name, catMap);
        if (!aid) console.warn("[BatchUpload] Account not found:", JSON.stringify(item.account_name));
        return { ...item, account_id: aid, category_id: cid };
      });

      const issueDate = parseExcelDate(bill.issue_date);
      const dueDate = parseExcelDate(bill.due_date);

      const updated = { ...bill, partner_id: pid, issue_date: issueDate, due_date: dueDate, items: resolvedItems };
      return recalcBillStatus(updated);
    });
  };

  const recalcBillStatus = (b) => {
    const errs = [];
    if (!b.partner_id) errs.push("Missing partner");
    if (!b.issue_date) errs.push("Missing issue date");
    b.items.forEach(i => {
      if (!i.account_id) errs.push(`Account missing for "${i.product_name || i.description || "item"}"`);
      if (!i.description && !i.product_name) errs.push("Item missing description");
    });
    return { ...b, errors: errs, status: errs.length ? "error" : "ready" };
  };

  const handlePreview = () => {
    if (!columnMapping.partner_name && !columnMapping.client && !columnMapping.bill_number && !defaultValues.partner_name && !defaultValues.client) {
      message.warning("Please map or provide a default for at least Bill Number or Partner/Client");
      return;
    }
    if (!dataLoaded) {
      message.warning("Reference data is still loading — please wait a moment and try again");
      return;
    }
    const grouped = groupRowsIntoBills(excelRows, columnMapping, defaultValues);
    const resolved = resolvePartnerAndAccounts(grouped);
    setBills(resolved);
    setStep(3);
  };

  const updateBillField = (billKey, field, value) => {
    setBills(prev => prev.map(b => {
      if (b.key !== billKey) return b;
      return recalcBillStatus({ ...b, [field]: value });
    }));
  };

  const updateItemField = (billKey, itemKey, field, value) => {
    setBills(prev => prev.map(b => {
      if (b.key !== billKey) return b;
      const items = b.items.map(i => {
        if (i.key !== itemKey) return i;
        const updated = { ...i, [field]: value };
        if (field === "quantity" || field === "unit_price") {
          updated.subtotal = (updated.quantity || 0) * (updated.unit_price || 0);
          updated.total = updated.subtotal;
        }
        return updated;
      });
      return recalcBillStatus({ ...b, items });
    }));
  };

  const removeBill = (billKey) => {
    setBills(prev => prev.filter(b => b.key !== billKey));
  };

  const handleSubmit = async () => {
    const readyBills = bills.filter(b => b.status === "ready");
    if (!readyBills.length) {
      message.error(`No valid ${typeLabel.toLowerCase()}s to submit. Fix the errors first.`);
      return;
    }

    setSaving(true);
    const submitResults = { success: 0, failed: 0, errors: [] };

    for (const bill of readyBills) {
      try {
        const subtotal = bill.items.reduce((sum, i) => sum + (i.subtotal || 0), 0);
        const totalAmount = bill.items.reduce((sum, i) => sum + (i.total || 0), 0);

        const safeIssueDate = parseExcelDate(bill.issue_date) || bill.issue_date;

        const computeDueDays = (item) => {
          const itemDue = item.due_date ? (parseExcelDate(item.due_date) || item.due_date) : null;
          if (itemDue && safeIssueDate) {
            const diff = dayjs(itemDue).diff(dayjs(safeIssueDate), "day");
            return Math.max(diff, 1);
          }
          return 30;
        };

        const itemDueDaysArr = bill.items.map(computeDueDays);
        const allSameDueDays = itemDueDaysArr.every(d => d === itemDueDaysArr[0]);

        const items = bill.items.map((item, idx) => {
          const base = {
            account: item.account_id,
            description: item.description || "",
            product_name: item.product_name || "",
            product_variation: "",
            quantity: item.quantity || 1,
            unit_price: Number((item.unit_price || 0).toFixed(3)),
            tax_rate: 0,
            tax_amount: 0,
            subtotal: Number((item.subtotal || 0).toFixed(3)),
            total: Number((item.total || 0).toFixed(3)),
            product_type: item.product_type || "product",
            ...(item.category_id ? { category: item.category_id } : {}),
          };

          if (!allSameDueDays) {
            const dueDays = itemDueDaysArr[idx];
            base.payment_terms = [{
              percentage: 100,
              due_days: dueDays,
              description: `Net ${dueDays}`,
              active: true,
            }];
          }

          return base;
        });

        let globalPaymentTerms;
        if (allSameDueDays) {
          const dueDays = itemDueDaysArr[0] || 30;
          globalPaymentTerms = [{
            percentage: 100,
            due_days: dueDays,
            active: true,
          }];
        } else {
          const dueDaysGroups = {};
          bill.items.forEach((item, idx) => {
            const dd = itemDueDaysArr[idx];
            const itemTotal = item.total || item.subtotal || 0;
            if (!dueDaysGroups[dd]) dueDaysGroups[dd] = 0;
            dueDaysGroups[dd] += itemTotal;
          });

          globalPaymentTerms = Object.entries(dueDaysGroups).map(([dd, amount]) => {
            const pct = totalAmount > 0 ? Number(((amount / totalAmount) * 100).toFixed(2)) : 0;
            return {
              percentage: pct,
              due_days: Number(dd),
              active: true,
            };
          });

          const pctSum = globalPaymentTerms.reduce((s, t) => s + t.percentage, 0);
          if (globalPaymentTerms.length > 0 && Math.abs(pctSum - 100) > 0.01) {
            globalPaymentTerms[globalPaymentTerms.length - 1].percentage += Number((100 - pctSum).toFixed(2));
          }
        }

        const invoiceData = {
          partner: bill.partner_id,
          issue_date: safeIssueDate,
          invoice_type: invoiceType,
          number: bill.number,
          currency: bill.currency === "USD" ? "$" : bill.currency,
          status: "draft",
          subtotal: Number(subtotal.toFixed(3)),
          tax_rate: 0,
          tax_amount: 0,
          discount_amount: 0,
          total_amount: Number(totalAmount.toFixed(3)),
          notes: "",
          brand: selectedBrand?.id,
          items: JSON.stringify(items),
          payment_terms: JSON.stringify(globalPaymentTerms),
        };

        const formData = new FormData();
        Object.entries(invoiceData).forEach(([k, v]) => {
          if (v !== undefined && v !== null) formData.append(k, v);
        });

        await createInvoiceWithFile(formData);
        submitResults.success++;
      } catch (err) {
        submitResults.failed++;
        let errMsg = "";
        const data = err.response?.data;
        if (data && typeof data === "object") {
          const flatten = (v) => {
            if (typeof v === "string") return v;
            if (Array.isArray(v)) return v.map(flatten).join(", ");
            if (v && typeof v === "object") {
              return Object.entries(v).map(([k, val]) => `${k}: ${flatten(val)}`).join("; ");
            }
            return String(v);
          };
          const parts = [];
          for (const [key, val] of Object.entries(data)) {
            parts.push(`${key}: ${flatten(val)}`);
          }
          errMsg = parts.join(" | ");
        } else {
          errMsg = err.message;
        }
        submitResults.errors.push(`${typeLabel} #${bill.number}: ${errMsg}`);
      }
    }

    setResults(submitResults);
    setSaving(false);

    if (submitResults.success > 0) {
      message.success(`Created ${submitResults.success} ${typeLabel.toLowerCase()}${submitResults.success > 1 ? "s" : ""}${submitResults.failed > 0 ? ` (${submitResults.failed} failed)` : ""}`);
    } else {
      message.error(`All ${typeLabel.toLowerCase()} uploads failed`);
    }
  };

  const totalErrors = useMemo(() => bills.filter(b => b.status === "error").length, [bills]);
  const totalReady = useMemo(() => bills.filter(b => b.status === "ready").length, [bills]);

  const renderAccountOptions = () => {
    if (!accounts.length) return null;
    return accounts.map(root => (
      <OptGroup key={root.id} label={`${root.code || ""} - ${root.name}`}>
        {flattenAccountTree([root], 0).filter(a => a.depth > 0).map(a => (
          <Option key={a.id} value={a.id} disabled={!a.selectable}>
            {"— ".repeat(a.depth - 1)}{a.code ? `${a.code} - ${a.name}` : a.name}
          </Option>
        ))}
      </OptGroup>
    ));
  };

  const billColumns = [
    {
      title: `${typeLabel} #`,
      dataIndex: "number",
      key: "number",
      width: 120,
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "Partner",
      key: "partner",
      width: 220,
      render: (_, record) => (
        <Select
          size="small" style={{ width: "100%" }} placeholder="Select partner..."
          showSearch optionFilterProp="children"
          value={record.partner_id || undefined}
          onChange={(v) => updateBillField(record.key, "partner_id", v)}
          status={!record.partner_id ? "error" : undefined}
        >
          {partners.map(p => (
            <Option key={p.id} value={p.id}>{p.name}{p.company_name ? ` (${p.company_name})` : ""}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Issue Date",
      dataIndex: "issue_date",
      key: "issue_date",
      width: 110,
      render: (val) => val ? <Tag>{val}</Tag> : <Tag color="red">Missing</Tag>,
    },
    {
      title: "Items",
      key: "item_count",
      width: 60,
      render: (_, r) => <Tag color="blue">{r.items.length}</Tag>,
    },
    {
      title: "Total",
      key: "total",
      width: 140,
      render: (_, r) => {
        const calc = r.items.reduce((s, i) => s + (i.total || 0), 0);
        const mismatch = r.spreadsheet_total != null && Math.abs(calc - r.spreadsheet_total) > 0.01;
        return (
          <span>
            <Text strong>${calc.toFixed(2)}</Text>
            {mismatch && <Tooltip title={`Spreadsheet says $${r.spreadsheet_total.toFixed(2)}`}><Tag color="orange" style={{ marginLeft: 4, fontSize: 10 }}>!</Tag></Tooltip>}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, r) => r.status === "ready"
        ? <Tag icon={<CheckCircleOutlined />} color="success">Ready</Tag>
        : <Tooltip title={r.errors.join("; ")}><Tag icon={<WarningOutlined />} color="error">{r.errors.length} issue{r.errors.length > 1 ? "s" : ""}</Tag></Tooltip>,
    },
    {
      title: "",
      key: "actions",
      width: 40,
      render: (_, r) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeBill(r.key)} />
      ),
    },
  ];

  const expandedRowRender = (bill) => {
    const itemCols = [
      { title: "Product Name", dataIndex: "product_name", key: "product_name", width: 150,
        render: (v, r) => <Input size="small" value={v} onChange={e => updateItemField(bill.key, r.key, "product_name", e.target.value)} />,
      },
      { title: "Description", dataIndex: "description", key: "description",
        render: (v, r) => <Input size="small" value={v} onChange={e => updateItemField(bill.key, r.key, "description", e.target.value)} />,
      },
      { title: "Account", key: "account", width: 220,
        render: (_, r) => (
          <Select size="small" style={{ width: "100%" }} showSearch optionFilterProp="children"
            value={r.account_id || undefined} onChange={v => updateItemField(bill.key, r.key, "account_id", v)}
            status={!r.account_id ? "error" : undefined} placeholder="Select account..."
          >
            {renderAccountOptions()}
          </Select>
        ),
      },
      { title: "Category", key: "category", width: 150,
        render: (_, r) => (
          <Select size="small" style={{ width: "100%" }} showSearch optionFilterProp="children" allowClear
            value={r.category_id || undefined} onChange={v => updateItemField(bill.key, r.key, "category_id", v)}
            placeholder="Category..."
          >
            {categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        ),
      },
      { title: "Qty", dataIndex: "quantity", key: "quantity", width: 80,
        render: (v, r) => <InputNumber size="small" style={{ width: "100%" }} min={0} value={v} onChange={val => updateItemField(bill.key, r.key, "quantity", val)} />,
      },
      { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", width: 100,
        render: (v, r) => <InputNumber size="small" style={{ width: "100%" }} min={0} step={0.01} prefix="$" value={v} onChange={val => updateItemField(bill.key, r.key, "unit_price", val)} />,
      },
      { title: "Subtotal", key: "subtotal", width: 90,
        render: (_, r) => <Text>${(r.subtotal || 0).toFixed(2)}</Text>,
      },
    ];
    return <Table dataSource={bill.items} columns={itemCols} rowKey="key" pagination={false} size="small" />;
  };

  const modalWidth = step === 1 ? 550 : step === 2 ? 800 : 1300;

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined />
          <span>Batch Upload {typeLabel}s</span>
          {step > 1 && <Tag color="blue">Step {step} of 3</Tag>}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={modalWidth}
      destroyOnClose
      zIndex={2000}
      footer={
        step === 1 ? null :
        step === 2 ? [
          <Button key="back" onClick={() => setStep(1)}>Back</Button>,
          <Button key="preview" type="primary" onClick={handlePreview} loading={!dataLoaded}>
            {dataLoaded ? `Preview ${typeLabel}s` : "Loading data..."}
          </Button>,
        ] :
        results ? [
          <Button key="back" onClick={() => { setResults(null); setStep(3); }}>Back to Preview</Button>,
          <Button key="retry" onClick={() => { setResults(null); }}>Try Again</Button>,
          <Button key="done" type="primary" onClick={() => { onSuccess?.(); onCancel(); }}>
            Done
          </Button>,
        ] : [
          <Button key="back" onClick={() => setStep(2)}>Back to Mapping</Button>,
          <Button key="submit" type="primary" onClick={handleSubmit} loading={saving}
            disabled={totalReady === 0}
          >
            Create {totalReady} {typeLabel}{totalReady !== 1 ? "s" : ""}
          </Button>,
        ]
      }
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      {/* Step 1: Upload */}
      {step === 1 && (
        <div>
          <Alert
            message={`Upload ${typeLabel}s from CSV or Excel`}
            description={
              <div>
                <p style={{ margin: "4px 0" }}>Your file should have columns like: <strong>Partner</strong>, <strong>Issue Date</strong>, <strong>Chart of Account</strong>, <strong>Product Name</strong>, <strong>Description</strong>, <strong>Quantity</strong>, <strong>Unit Price</strong>, etc.</p>
                <p style={{ margin: "4px 0" }}>Multiple products per {typeLabel.toLowerCase()} are supported — leave the header fields blank on continuation rows.</p>
              </div>
            }
            type="info" showIcon style={{ marginBottom: 16 }}
          />
          <Dragger
            accept=".csv,.xlsx,.xls"
            fileList={fileList}
            beforeUpload={handleFileUpload}
            onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
            maxCount={1}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag CSV / Excel file to upload</p>
            <p className="ant-upload-hint">Supports .csv, .xlsx, .xls</p>
          </Dragger>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 2 && (
        <div>
          <Alert message={`Found ${excelRows.length} rows and ${excelColumns.length} columns`} type="success" showIcon style={{ marginBottom: 16 }} />
          {loadErrors.length > 0 && (
            <Alert message={`Failed to load: ${loadErrors.join(", ")}. Partner and account matching may not work.`} type="error" showIcon style={{ marginBottom: 16 }} />
          )}
          {!dataLoaded && !loadErrors.length && (
            <Alert message="Loading partners, accounts and categories..." type="info" showIcon style={{ marginBottom: 16 }} />
          )}
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Map your columns or type a default value:</div>
          <Table
            dataSource={Object.entries(EXPECTED_COLUMNS).map(([field, config]) => ({
              key: field, field, label: config.label,
            }))}
            columns={[
              { title: "DataVision Field", dataIndex: "label", key: "label", width: 180,
                render: (label, r) => (
                  <span>{label} {["partner_name", "issue_date", "account_name", "quantity", "unit_price"].includes(r.field) && <Tag color="red" style={{ fontSize: 10 }}>Required</Tag>}</span>
                ),
              },
              { title: "Excel Column", key: "mapping", width: 200, render: (_, r) => (
                  <Select size="small" style={{ width: "100%" }} allowClear placeholder="Select column..."
                    value={columnMapping[r.field]} onChange={v => handleMappingChange(r.field, v)}
                  >
                    {excelColumns.map(c => <Option key={c} value={c}>{c}</Option>)}
                  </Select>
                ),
              },
              { title: "Default / Override", key: "default", width: 180, render: (_, r) => (
                  <Input size="small" placeholder={columnMapping[r.field] ? "Override..." : "Type value..."}
                    value={defaultValues[r.field] || ""}
                    onChange={e => setDefaultValues(prev => ({ ...prev, [r.field]: e.target.value }))}
                  />
                ),
              },
              { title: "Sample", key: "sample", width: 160, render: (_, r) => {
                  const col = columnMapping[r.field];
                  const def = defaultValues[r.field];
                  if (def) return <Tag color="blue" style={{ fontSize: 11 }}>{def}</Tag>;
                  if (!col || !excelRows[0]) return <Text type="secondary">—</Text>;
                  return <Text type="secondary" style={{ fontSize: 12 }}>{String(excelRows[0][col]).substring(0, 30)}</Text>;
                },
              },
            ]}
            pagination={false} size="small"
          />
        </div>
      )}

      {/* Step 3: Preview & Submit */}
      {step === 3 && !results && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px" }}>{bills.length} {typeLabel}{bills.length !== 1 ? "s" : ""}</Tag>
            <Tag color="green" style={{ fontSize: 13, padding: "4px 12px" }}>{totalReady} Ready</Tag>
            {totalErrors > 0 && <Tag color="red" style={{ fontSize: 13, padding: "4px 12px" }}>{totalErrors} With Issues</Tag>}
          </div>
          {totalErrors > 0 && (
            <Alert
              message={`${totalErrors} ${typeLabel.toLowerCase()}${totalErrors > 1 ? "s have" : " has"} issues that need fixing`}
              description={`Fix the highlighted fields below, or remove the problematic ${typeLabel.toLowerCase()}s. Only 'Ready' ${typeLabel.toLowerCase()}s will be created.`}
              type="warning" showIcon style={{ marginBottom: 16 }}
            />
          )}
          <Table
            dataSource={bills}
            columns={billColumns}
            rowKey="key"
            expandable={{ expandedRowRender, defaultExpandAllRows: bills.length <= 5 }}
            pagination={bills.length > 20 ? { pageSize: 20 } : false}
            size="small"
          />
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          <Alert
            message={`Upload Complete: ${results.success} created, ${results.failed} failed`}
            type={results.failed === 0 ? "success" : "warning"}
            showIcon style={{ marginBottom: 16 }}
          />
          {results.errors.length > 0 && (
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {results.errors.map((err, i) => (
                <Alert key={i} message={err} type="error" style={{ marginBottom: 4 }} />
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default BatchUploadBillsInvoices;
